import yaml from 'js-yaml';

export type TenantConfigDoc = Record<string, unknown>;

export function parseTenantYaml(content: string): TenantConfigDoc {
  const parsed = yaml.load(content);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('Tenant YAML must be a mapping at the root');
  }
  return parsed as TenantConfigDoc;
}

export function tenantDocToYaml(doc: TenantConfigDoc): string {
  return yaml.dump(doc, { lineWidth: 120, noRefs: true, skipInvalid: true });
}

export function cloneDoc<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function ensureObject(parent: Record<string, unknown>, key: string): Record<string, unknown> {
  const existing = parent[key];
  if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
    return existing as Record<string, unknown>;
  }
  const next: Record<string, unknown> = {};
  parent[key] = next;
  return next;
}

export function ensureArray(parent: Record<string, unknown>, key: string): unknown[] {
  const existing = parent[key];
  if (Array.isArray(existing)) return existing;
  const next: unknown[] = [];
  parent[key] = next;
  return next;
}

export function asString(value: unknown): string {
  if (value == null) return '';
  return String(value);
}

export function asBool(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value;
  return fallback;
}

export function asNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((v) => String(v ?? ''));
}

export function objectMap(value: unknown): Record<string, Record<string, unknown>> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, Record<string, unknown>> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      out[k] = v as Record<string, unknown>;
    } else {
      out[k] = {};
    }
  }
  return out;
}

export function objectRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
}

/** Tenant YAML may mark a master product as associated without copying its full definition. */
export function isProductAssociationMarker(config: unknown): boolean {
  if (!config || typeof config !== 'object' || Array.isArray(config)) return false;
  const obj = config as Record<string, unknown>;
  const keys = Object.keys(obj);
  if (keys.length === 0) return true;
  return keys.length === 1 && obj.fromMaster === true;
}
